/** All sound is synthesised with WebAudio: no asset files. */
export class Audio {
  ctx: AudioContext | null = null;
  master: GainNode | null = null;
  ambient: GainNode | null = null;
  heartGain: GainNode | null = null;
  droneOsc: OscillatorNode[] = [];
  enabled = true;
  private background = false;
  private heartTimer = 0;
  private lastStep = 0;

  /** must be called from a user gesture */
  init(): void {
    if (this.ctx) return;
    try {
      const AC = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      this.ctx = new AC();
      this.master = this.ctx.createGain();
      this.master.gain.value = this.background ? 0 : 0.5;
      this.master.connect(this.ctx.destination);
      this.startAmbient();
    } catch {
      this.enabled = false;
    }
  }
  resume(): void {
    if (this.ctx && this.ctx.state === 'suspended') void this.ctx.resume();
  }
  setBackground(background: boolean): void {
    this.background = background;
    if (!this.ctx || !this.master) return;
    const t = this.ctx.currentTime, gain = this.master.gain;
    gain.cancelScheduledValues(t);
    gain.setValueAtTime(gain.value, t);
    gain.linearRampToValueAtTime(background ? 0 : 0.5, t + 0.08);
  }
  private now(): number {
    return this.ctx!.currentTime;
  }
  private noiseBuffer(): AudioBuffer {
    const ctx = this.ctx!;
    const b = ctx.createBuffer(1, ctx.sampleRate * 1, ctx.sampleRate);
    const d = b.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
    return b;
  }
  private _noise: AudioBuffer | null = null;
  private noise(): AudioBufferSourceNode {
    const ctx = this.ctx!;
    if (!this._noise) this._noise = this.noiseBuffer();
    const s = ctx.createBufferSource();
    s.buffer = this._noise;
    s.loop = true;
    return s;
  }
  private env(g: GainNode, t0: number, a: number, peak: number, d: number): void {
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.linearRampToValueAtTime(peak, t0 + a);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + a + d);
  }

  startAmbient(): void {
    const ctx = this.ctx!;
    this.ambient = ctx.createGain();
    this.ambient.gain.value = 0.12;
    this.ambient.connect(this.master!);
    // low drone
    for (const [f, type] of [[38, 'sine'], [57, 'triangle'], [77, 'sine']] as Array<[number, OscillatorType]>) {
      const o = ctx.createOscillator();
      o.type = type;
      o.frequency.value = f;
      const g = ctx.createGain();
      g.gain.value = 0.25;
      const lfo = ctx.createOscillator();
      lfo.frequency.value = 0.05 + Math.random() * 0.1;
      const lg = ctx.createGain();
      lg.gain.value = 0.15;
      lfo.connect(lg).connect(g.gain);
      lfo.start();
      o.connect(g).connect(this.ambient);
      o.start();
      this.droneOsc.push(o);
    }
    // wind: filtered noise
    const n = this.noise();
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = 400;
    bp.Q.value = 0.6;
    const g = ctx.createGain();
    g.gain.value = 0.08;
    const lfo = ctx.createOscillator();
    lfo.frequency.value = 0.07;
    const lg = ctx.createGain();
    lg.gain.value = 0.05;
    lfo.connect(lg).connect(g.gain);
    lfo.start();
    n.connect(bp).connect(g).connect(this.ambient);
    n.start();
    // heartbeat bus
    this.heartGain = ctx.createGain();
    this.heartGain.gain.value = 0;
    this.heartGain.connect(this.master!);
  }

  step(gait: 'sneak' | 'walk' | 'run', vol = 1): void {
    if (!this.ctx) return;
    const t = this.now();
    if (t - this.lastStep < 0.08) return;
    this.lastStep = t;
    const ctx = this.ctx;
    const n = this.noise();
    const f = ctx.createBiquadFilter();
    f.type = 'lowpass';
    f.frequency.value = gait === 'run' ? 900 : gait === 'walk' ? 600 : 350;
    const g = ctx.createGain();
    const peak = (gait === 'run' ? 0.35 : gait === 'walk' ? 0.16 : 0.05) * vol;
    this.env(g, t, 0.005, peak, 0.08);
    n.connect(f).connect(g).connect(this.master!);
    n.start(t);
    n.stop(t + 0.15);
  }
  door(volume = 1, pan = 0): void {
    if (!this.ctx) return;
    const ctx = this.ctx, t = this.now();
    const o = ctx.createOscillator();
    o.type = 'sawtooth';
    o.frequency.setValueAtTime(180, t);
    o.frequency.exponentialRampToValueAtTime(90, t + 0.5);
    const f = ctx.createBiquadFilter();
    f.type = 'lowpass';
    f.frequency.value = 700;
    const g = ctx.createGain();
    this.env(g, t, 0.05, 0.12 * volume, 0.5);
    const stereo = ctx.createStereoPanner(); stereo.pan.value = pan;
    o.connect(f).connect(g).connect(stereo).connect(this.master!);
    o.start(t);
    o.stop(t + 0.6);
  }
  pickup(): void {
    this.blip(660, 880, 0.12, 0.15);
  }
  ui(): void {
    this.blip(440, 520, 0.06, 0.08);
  }
  deny(): void {
    this.blip(220, 160, 0.15, 0.12);
  }
  unlock(): void {
    this.blip(300, 150, 0.2, 0.2);
    setTimeout(() => this.blip(500, 700, 0.12, 0.15), 120);
  }
  private blip(f0: number, f1: number, d: number, vol: number): void {
    if (!this.ctx) return;
    const ctx = this.ctx, t = this.now();
    const o = ctx.createOscillator();
    o.type = 'square';
    o.frequency.setValueAtTime(f0, t);
    o.frequency.exponentialRampToValueAtTime(f1, t + d);
    const g = ctx.createGain();
    this.env(g, t, 0.005, vol, d);
    o.connect(g).connect(this.master!);
    o.start(t);
    o.stop(t + d + 0.05);
  }
  growl(vol = 1): void {
    if (!this.ctx) return;
    const ctx = this.ctx, t = this.now();
    const o = ctx.createOscillator();
    o.type = 'sawtooth';
    o.frequency.setValueAtTime(55, t);
    o.frequency.linearRampToValueAtTime(42, t + 0.9);
    const o2 = ctx.createOscillator();
    o2.type = 'square';
    o2.frequency.value = 27;
    const f = ctx.createBiquadFilter();
    f.type = 'lowpass';
    f.frequency.setValueAtTime(400, t);
    f.frequency.linearRampToValueAtTime(150, t + 1);
    const g = ctx.createGain();
    this.env(g, t, 0.1, 0.5 * vol, 1.0);
    o.connect(f);
    o2.connect(f);
    f.connect(g).connect(this.master!);
    o.start(t); o2.start(t);
    o.stop(t + 1.2); o2.stop(t + 1.2);
  }
  scream(): void {
    if (!this.ctx) return;
    const ctx = this.ctx, t = this.now();
    const o = ctx.createOscillator();
    o.type = 'sawtooth';
    o.frequency.setValueAtTime(700, t);
    o.frequency.exponentialRampToValueAtTime(1200, t + 0.15);
    o.frequency.exponentialRampToValueAtTime(500, t + 0.6);
    const g = ctx.createGain();
    this.env(g, t, 0.02, 0.25, 0.6);
    o.connect(g).connect(this.master!);
    o.start(t);
    o.stop(t + 0.7);
  }
  shatter(): void {
    if (!this.ctx) return;
    const ctx = this.ctx, t = this.now();
    const n = this.noise();
    const f = ctx.createBiquadFilter();
    f.type = 'highpass';
    f.frequency.value = 2500;
    const g = ctx.createGain();
    this.env(g, t, 0.005, 0.5, 0.35);
    n.connect(f).connect(g).connect(this.master!);
    n.start(t);
    n.stop(t + 0.4);
  }
  bang(): void {
    if (!this.ctx) return;
    const ctx = this.ctx, t = this.now();
    const n = this.noise();
    const f = ctx.createBiquadFilter();
    f.type = 'lowpass';
    f.frequency.setValueAtTime(3000, t);
    f.frequency.exponentialRampToValueAtTime(200, t + 0.5);
    const g = ctx.createGain();
    this.env(g, t, 0.005, 0.9, 0.6);
    n.connect(f).connect(g).connect(this.master!);
    n.start(t);
    n.stop(t + 0.7);
  }
  flash(): void {
    this.blip(1200, 2400, 0.08, 0.2);
  }
  thud(): void {
    if (!this.ctx) return;
    const ctx = this.ctx, t = this.now();
    const o = ctx.createOscillator();
    o.type = 'sine';
    o.frequency.setValueAtTime(120, t);
    o.frequency.exponentialRampToValueAtTime(40, t + 0.25);
    const g = ctx.createGain();
    this.env(g, t, 0.005, 0.6, 0.3);
    o.connect(g).connect(this.master!);
    o.start(t);
    o.stop(t + 0.35);
  }
  impact(material: 'metal' | 'glass' | 'soft', volume = 1): void {
    if (!this.ctx) return;
    const ctx = this.ctx, t = this.now();
    const pitches = material === 'metal' ? [380, 631, 1073] : material === 'glass' ? [1200, 1780, 2630] : [95, 140];
    pitches.forEach((pitch, i) => {
      const oscillator = ctx.createOscillator(), gain = ctx.createGain();
      oscillator.type = 'sine';
      oscillator.frequency.value = pitch;
      this.env(gain, t, 0.002, 0.2 * volume / (i + 1), material === 'soft' ? 0.07 : 0.28 / (i + 1));
      oscillator.connect(gain).connect(this.master!);
      oscillator.start(t); oscillator.stop(t + 0.4);
    });
  }
  scrape(pan = 0, volume = 1): void {
    if (!this.ctx) return;
    const ctx = this.ctx, t = this.now();
    const source = this.noise(), filter = ctx.createBiquadFilter(), gain = ctx.createGain(), stereo = ctx.createStereoPanner();
    filter.type = 'bandpass'; filter.frequency.setValueAtTime(460, t); filter.frequency.linearRampToValueAtTime(1200, t + 0.75); filter.Q.value = 3;
    stereo.pan.value = pan;
    this.env(gain, t, 0.08, 0.32 * volume, 0.85);
    source.connect(filter).connect(gain).connect(stereo).connect(this.master!);
    source.start(t); source.stop(t + 1.1);
  }
  monsterStep(pan: number, volume: number, muffled: boolean): void {
    if (!this.ctx || volume <= 0) return;
    const ctx = this.ctx, t = this.now(), tone = ctx.createOscillator(), gain = ctx.createGain(), stereo = ctx.createStereoPanner();
    tone.type = 'sine'; tone.frequency.setValueAtTime(muffled ? 58 : 82, t); tone.frequency.exponentialRampToValueAtTime(32, t + 0.22);
    stereo.pan.value = pan;
    this.env(gain, t, 0.006, 0.65 * volume, 0.3);
    tone.connect(gain).connect(stereo).connect(this.master!); tone.start(t); tone.stop(t + 0.35);
    const scrape = this.noise(), filter = ctx.createBiquadFilter(), scrapeGain = ctx.createGain();
    filter.type = 'lowpass'; filter.frequency.value = muffled ? 120 : 380;
    this.env(scrapeGain, t + 0.025, 0.015, 0.15 * volume, 0.12);
    scrape.connect(filter).connect(scrapeGain).connect(stereo); scrape.start(t); scrape.stop(t + 0.2);
  }
  power(): void {
    if (!this.ctx) return;
    const ctx = this.ctx, t = this.now();
    const o = ctx.createOscillator();
    o.type = 'sawtooth';
    o.frequency.setValueAtTime(40, t);
    o.frequency.linearRampToValueAtTime(60, t + 1.5);
    const g = ctx.createGain();
    this.env(g, t, 0.3, 0.3, 1.5);
    o.connect(g).connect(this.master!);
    o.start(t);
    o.stop(t + 2);
  }
  thunder(): void {
    if (!this.ctx) return;
    const ctx = this.ctx, t = this.now();
    const n = this.noise();
    const f = ctx.createBiquadFilter();
    f.type = 'lowpass';
    f.frequency.setValueAtTime(600, t);
    f.frequency.exponentialRampToValueAtTime(80, t + 2.5);
    const g = ctx.createGain();
    this.env(g, t, 0.15, 0.5, 2.5);
    n.connect(f).connect(g).connect(this.master!);
    n.start(t);
    n.stop(t + 3);
  }
  jingle(win: boolean): void {
    const notes = win ? [523, 659, 784, 1046] : [440, 415, 392, 330];
    notes.forEach((n, i) => setTimeout(() => this.blip(n, n, 0.25, 0.2), i * 180));
  }
  /** heartbeat: `level` 0..1 drives volume and tempo */
  heartbeat(level: number, dt: number): void {
    if (!this.ctx || !this.heartGain) return;
    this.heartGain.gain.setTargetAtTime(level * 0.8, this.now(), 0.1);
    if (level <= 0.02) return;
    const period = 1.1 - level * 0.7;
    this.heartTimer -= dt;
    if (this.heartTimer <= 0) {
      this.heartTimer = period;
      this.thump(0);
      setTimeout(() => this.thump(0.6), 160);
    }
  }
  private thump(v: number): void {
    if (!this.ctx || !this.heartGain) return;
    const ctx = this.ctx, t = this.now();
    const o = ctx.createOscillator();
    o.type = 'sine';
    o.frequency.setValueAtTime(70, t);
    o.frequency.exponentialRampToValueAtTime(35, t + 0.18);
    const g = ctx.createGain();
    this.env(g, t, 0.005, 0.9 - v * 0.3, 0.2);
    o.connect(g).connect(this.heartGain);
    o.start(t);
    o.stop(t + 0.25);
  }
  setAmbient(v: number): void {
    if (this.ambient && this.ctx) this.ambient.gain.setTargetAtTime(v, this.ctx.currentTime, 0.5);
  }
}
